import { AlertMessage, useAlertConfirm } from "../../components/alerts";
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Clock3,
  Snowflake,
  Timer,
  Play,
  Pause,
  Square,
  Eye,
  Save,
  Settings2,
  Check,
  Trophy,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { ContestStateDto } from "@compila/shared";
import { api, ApiError } from "../../lib/api.js";
import { useContestClock } from "../../lib/hooks.js";
import { Button } from "../../components/motion/button/base";
import { Input } from "../../components/motion/input";
import { formatClock } from "../../lib/format.js";

const labels = {
  DRAFT: "Não iniciada",
  RUNNING: "Em andamento",
  PAUSED: "Pausada",
  ENDED: "Encerrada",
};
const fieldStyles = {
  field: "rounded-xl bg-background",
  label: "px-0",
  input: "placeholder:text-muted-foreground",
};

function Notice({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return <AlertMessage tone={error ? "error" : "info"}>{children}</AlertMessage>;
}

export function AdminContestPage(): React.JSX.Element {
  const confirmAlert = useAlertConfirm();

  const queryClient = useQueryClient();
  const contest = useQuery({
    queryKey: ["contest"],
    queryFn: () => api.get<ContestStateDto>("/contest"),
  });
  const elapsed = useContestClock(contest.data);
  const action = useMutation({
    mutationFn: (route: string) => api.post<ContestStateDto>(`/admin/contest/${route}`),
    onSuccess: (state) => {
      queryClient.setQueryData(["contest"], state);
      void queryClient.invalidateQueries({ queryKey: ["admin-scoreboard"] });
    },
  });
  if (contest.isPending)
    return (
      <p role="status" className="py-16 text-center text-muted-foreground">
        Carregando competição…
      </p>
    );
  if (contest.error)
    return (
      <div className="space-y-4">
        <Notice error>{contest.error.message}</Notice>
        <Button variant="secondary" onClick={() => void contest.refetch()}>
          Tentar novamente
        </Button>
      </div>
    );
  const c = contest.data;
  const remaining = Math.max(0, c.durationSeconds - elapsed);
  const progress = Math.min(
    100,
    Math.max(0, (elapsed / Math.max(1, c.durationSeconds)) * 100),
  );
  const confirm = async (message: string, route: string) => {
    if (!action.isPending && (await confirmAlert(message))) action.mutate(route);
  };
  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-semibold tracking-tight">Competição</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gerencie o tempo, as regras e o andamento da maratona.
          </p>
        </div>
        <Link
          to="/telao"
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm transition-colors hover:bg-secondary"
        >
          Abrir telão <ArrowUpRight size={16} aria-hidden />
        </Link>
      </div>

      <section
        aria-labelledby="contest-name"
        className="overflow-hidden rounded-2xl border border-border bg-card"
      >
        <div className="grid lg:grid-cols-[1fr_auto]">
          <div className="min-w-0 p-6 sm:p-8">
            <div className="mb-5 flex items-center gap-2 text-xs text-muted-foreground">
              <Trophy size={15} aria-hidden /> COMPETIÇÃO ATUAL
            </div>
            <h2
              id="contest-name"
              className="break-words text-2xl font-semibold tracking-tight"
            >
              {c.name}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs">
                <span
                  className={`size-1.5 rounded-full ${c.status === "RUNNING" ? "bg-accent" : "bg-muted-foreground"}`}
                />
                {labels[c.status]}
              </span>
              {c.frozen && (
                <span className="inline-flex items-center gap-1.5 text-xs text-accent">
                  <Snowflake size={13} aria-hidden />
                  Placar congelado
                </span>
              )}
            </div>
            <p className="mt-5 max-w-lg text-sm leading-6 text-muted-foreground">
              {c.status === "DRAFT"
                ? "Tudo pronto para começar? Confira os parâmetros antes de iniciar a competição."
                : c.status === "PAUSED"
                  ? "A competição está pausada. O cronômetro e o recebimento de submissões estão suspensos."
                  : c.status === "ENDED"
                    ? "A competição foi encerrada. Novas submissões não são mais recebidas."
                    : "A competição está em andamento. Acompanhe o tempo restante e gerencie a prova por aqui."}
            </p>
          </div>
          <div className="flex flex-col justify-center border-t border-border p-6 sm:p-8 lg:min-w-[300px] lg:border-l lg:border-t-0">
            <span className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Clock3 size={15} aria-hidden />
              Tempo restante
            </span>
            <span
              className={`font-mono text-4xl font-medium tracking-tight tabular-nums sm:text-5xl ${c.status === "RUNNING" && remaining <= 300 ? "text-destructive" : "text-foreground"}`}
            >
              {formatClock(remaining)}
            </span>
            <div
              className="mt-5 h-1.5 overflow-hidden rounded-full bg-background"
              role="progressbar"
              aria-label="Tempo decorrido"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress)}
            >
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {formatClock(elapsed)} decorridos de {formatClock(c.durationSeconds)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border px-6 py-5 sm:px-8">
          <span className="text-xs text-muted-foreground">
            {action.isPending ? "Aplicando alteração…" : "Controles da competição"}
          </span>
          <div className="flex flex-wrap gap-2">
            {c.status === "DRAFT" && (
              <Button
                className="rounded-xl"
                disabled={action.isPending}
                onClick={() =>
                  confirm(
                    "Iniciar a prova agora? O cronômetro começa imediatamente.",
                    "start",
                  )
                }
              >
                <Play size={15} aria-hidden />
                Iniciar competição
              </Button>
            )}
            {c.status === "RUNNING" && (
              <Button
                variant="secondary"
                className="rounded-xl"
                disabled={action.isPending}
                onClick={() =>
                  confirm(
                    "Pausar a prova? O cronômetro para para todas as equipes.",
                    "pause",
                  )
                }
              >
                <Pause size={15} aria-hidden />
                Pausar competição
              </Button>
            )}
            {c.status === "PAUSED" && (
              <Button
                className="rounded-xl"
                disabled={action.isPending}
                onClick={() => action.mutate("resume")}
              >
                <Play size={15} aria-hidden />
                Retomar competição
              </Button>
            )}
            {(c.status === "RUNNING" || c.status === "PAUSED") && (
              <Button
                variant="secondary"
                className="rounded-xl border-destructive/30"
                disabled={action.isPending}
                onClick={() =>
                  confirm(
                    "Encerrar a prova? Nenhuma equipe poderá enviar depois disso.",
                    "end",
                  )
                }
              >
                <Square size={14} className="text-destructive" aria-hidden />
                Encerrar competição
              </Button>
            )}
            {c.status === "ENDED" && !c.scoreboardRevealed && (
              <Button
                className="rounded-xl"
                disabled={action.isPending}
                onClick={() =>
                  confirm(
                    "Revelar o placar final? O público passa a ver os vereditos do período congelado.",
                    "reveal",
                  )
                }
              >
                <Eye size={16} aria-hidden />
                Revelar placar
              </Button>
            )}
            {c.status === "ENDED" && c.scoreboardRevealed && (
              <span className="inline-flex items-center gap-2 text-sm text-accent">
                <Check size={16} aria-hidden />
                Placar final revelado
              </span>
            )}
          </div>
        </div>
        {action.error && (
          <div className="px-6 pb-5 sm:px-8">
            <Notice error>
              {action.error instanceof ApiError
                ? action.error.message
                : "Falha ao executar a ação."}
            </Notice>
          </div>
        )}
      </section>

      <div className="grid gap-4 sm:grid-cols-3">
        <Metric
          icon={<Timer size={17} />}
          label="Duração da prova"
          value={`${c.durationSeconds / 60} min`}
          detail="Tempo total de competição"
        />
        <Metric
          icon={<Snowflake size={17} />}
          label="Congelamento do placar"
          value={`${c.freezeBeforeEndSeconds / 60} min`}
          detail={`Antes do fim · a partir de ${formatClock(c.freezeStartSeconds)}`}
        />
        <Metric
          icon={<Clock3 size={17} />}
          label="Penalidade por tentativa"
          value={`${c.penaltyMinutes} min`}
          detail="Para WA, TLE, MLE e RE"
        />
      </div>
      <Parameters contest={c} actionPending={action.isPending} />
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span aria-hidden>{icon}</span>
        {label}
      </div>
      <p className="mt-4 text-2xl font-semibold tabular-nums tracking-tight">{value}</p>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">{detail}</p>
    </section>
  );
}

function Parameters({
  contest,
  actionPending,
}: {
  contest: ContestStateDto;
  actionPending: boolean;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(contest.name);
  const [duration, setDuration] = useState(String(contest.durationSeconds / 60));
  const [freeze, setFreeze] = useState(String(contest.freezeBeforeEndSeconds / 60));
  const [penalty, setPenalty] = useState(String(contest.penaltyMinutes));
  const [dirty, setDirty] = useState(false);
  const draft = contest.status === "DRAFT";
  // Atualizações de relógio via SSE não devem apagar campos em edição.
  useEffect(() => {
    if (dirty) return;
    setName(contest.name);
    setDuration(String(contest.durationSeconds / 60));
    setFreeze(String(contest.freezeBeforeEndSeconds / 60));
    setPenalty(String(contest.penaltyMinutes));
  }, [
    contest.name,
    contest.durationSeconds,
    contest.freezeBeforeEndSeconds,
    contest.penaltyMinutes,
    dirty,
  ]);
  const save = useMutation({
    mutationFn: (confirm: boolean) =>
      api.patch<ContestStateDto>("/admin/contest", {
        ...(draft ? { name, durationSeconds: Math.round(Number(duration) * 60) } : {}),
        freezeBeforeEndSeconds: Math.round(Number(freeze) * 60),
        penaltyMinutes: Number(penalty),
        ...(confirm ? { confirm: true } : {}),
      }),
    onSuccess: (state) => {
      queryClient.setQueryData(["contest"], state);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-scoreboard"] });
    },
  });
  const change = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setDirty(true);
    save.reset();
  };
  const needsConfirmation =
    save.error instanceof ApiError && save.error.code === "CONTEST_LOCKED";
  const disabled = save.isPending || actionPending;
  return (
    <section
      className="rounded-2xl border border-border bg-card"
      aria-labelledby="parameters-title"
    >
      <div className="border-b border-border p-6">
        <h2
          id="parameters-title"
          className="flex items-center gap-2 text-base font-semibold"
        >
          <Settings2 size={18} className="text-muted-foreground" aria-hidden />
          Parâmetros da competição
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Defina as regras que serão aplicadas à prova.
        </p>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!disabled) save.mutate(needsConfirmation);
        }}
      >
        <div className="space-y-5 p-6">
          {!draft && (
            <Notice>
              Nome e duração ficam bloqueados após o início. Alterações no congelamento e
              na penalidade exigem confirmação e recalculam o placar.
            </Notice>
          )}
          <Input
            label="Nome da competição"
            value={name}
            onChange={change(setName)}
            required
            maxLength={200}
            disabled={!draft || disabled}
            classNames={fieldStyles}
          />
          <div className="grid gap-5 md:grid-cols-3">
            <div>
              <Input
                label="Duração (min)"
                type="number"
                min={1}
                max={1440}
                step={1}
                value={duration}
                onChange={change(setDuration)}
                required
                disabled={!draft || disabled}
                classNames={fieldStyles}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Duração total da prova.
              </p>
            </div>
            <div>
              <Input
                label="Congelamento (min)"
                type="number"
                min={0}
                step={1}
                value={freeze}
                onChange={change(setFreeze)}
                required
                disabled={disabled}
                classNames={fieldStyles}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Quantos minutos antes do fim.
              </p>
            </div>
            <div>
              <Input
                label="Penalidade (min)"
                type="number"
                min={0}
                max={1000}
                step={1}
                value={penalty}
                onChange={change(setPenalty)}
                required
                disabled={disabled}
                classNames={fieldStyles}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Por tentativa incorreta penalizável.
              </p>
            </div>
          </div>
          {save.error && (
            <Notice error>
              {save.error instanceof ApiError
                ? save.error.message
                : "Falha ao salvar os parâmetros."}
            </Notice>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-4">
          <span className="text-xs text-muted-foreground" role="status">
            {save.isSuccess
              ? "Parâmetros atualizados."
              : dirty
                ? "Você tem alterações não salvas."
                : "As configurações se aplicam a todas as equipes."}
          </span>
          <Button type="submit" className="rounded-xl" disabled={disabled}>
            <Save size={15} aria-hidden />
            {save.isPending
              ? "Salvando…"
              : needsConfirmation
                ? "Confirmar mesmo assim"
                : "Salvar alterações"}
          </Button>
        </div>
      </form>
    </section>
  );
}
